import {
  MULTIPLE_PROCEDURE_METHODOLOGY,
  ReimbursementSlot,
  reimbursementSlotLabel,
} from "pages/PayerRules/contracts/helpers";

import { DraftComponentType } from "../../../store/queries/contractModelDrafts";

/**
 * How to RENDER each `proposedModeling` field as an editable input.
 *
 * The authority on what is *valid* is the Zod schema in the API
 * (`contract_model_drafts.schemas.ts`) — the server re-validates every edit and 400s with a
 * field-scoped message. This file is purely presentation: which control, what label, what help text.
 * Labels and ordering are UI concerns a JSON Schema carries badly, so they live here.
 *
 * `fieldSpecs.test.ts` asserts every component type has a spec, so a new type can't ship as a blank card.
 */

export type FieldKind =
  | "text"
  | "textarea"
  | "number"
  | "money" // stored in CENTS; displayed and entered in dollars
  | "percent" // whole percent (102 = 102%)
  | "fraction" // decimal fraction (0.85 = 85%)
  | "date"
  | "select"
  | "boolean"
  | "codes" // string[] of CPT/HCPCS/DRG codes
  /**
   * A payer or provider. Rendered as a NAME with the id in parentheses — "Aetna (4)" — and NOT
   * editable. A raw id is meaningless to the reviewer, and letting her retype one invites a pointer to
   * a payer that doesn't exist, which is the exact mismatch the typed schema was built to eliminate.
   */
  | "entity"
  /**
   * Pick an EXISTING payer from the list. You must never be able to free-type the name of a payer that
   * already exists — that is how you end up pointing at one that doesn't. Selecting writes both the id
   * and the name, so the record reads properly and the schema (which wants a name) stays satisfied.
   */
  | "payerSelect"
  /**
   * The agent talking to the human — a note, a rationale. Nothing reads it when the construct is
   * built, so making it an input was a mistake: it invited editing of something with no effect.
   * Rendered as prose.
   */
  | "readonly"
  /**
   * A list of `{ code, amountInCents }` rows — a carveout's per-code dollar amounts. Rendered as an
   * add/remove table with a code field and a dollar field per row. Replaces raw-JSON editing, which
   * asked a non-technical reviewer to hand-write `[{"code":"30140","amountInCents":63700}]`.
   */
  | "moneyRows"
  /**
   * A list of `{ serviceLine, percent }` rows — the per-service-line multipliers of a single fee
   * schedule. Rendered as an add/remove table with a service-line field and a whole-percent field per
   * row (Lab 100%, Psychiatry 106%, …).
   */
  | "percentRows"
  /**
   * The display name of an EXISTING base rate file. Rendered as a filterable dropdown of the fee
   * schedules actually loaded for this account, so the reviewer picks a real file instead of
   * free-typing a name that may not exist (or, as here, one that is ambiguous across two files). The
   * agent's current value is always kept as an option so a proposal is never silently dropped.
   */
  | "baseRateFileSelect"
  /**
   * Like `baseRateFileSelect`, but for `reuse_existing`: the reviewer picks WHICH already-loaded file to
   * reference. Selecting one writes both the file's `displayName` (what Compare matches on) AND its
   * `baseRateFileId` (the exact file the schema requires) — so the id can never drift from the name.
   */
  | "baseRateFileReuseSelect";

export interface IFieldSpec {
  key: string;
  label: string;
  kind: FieldKind;
  /** Shown under the input. Use it for the unit traps — they are where real money is lost. */
  help?: string;
  options?: { label: string; value: string }[];
  required?: boolean;
  /**
   * Free text needs the whole row — in a shared-row cell it wraps into an unreadable column. Textareas,
   * read-only prose, code lists, row tables and long-labelled selects get it anyway (`isWideField`).
   */
  fullWidth?: boolean;
  /**
   * Only show this field when another field has one of these values.
   *
   * Without it, a routing decision renders every field for every branch at once: "Link an existing MA
   * payer" showed an editable name AND an editable id, and "Create an MA payer" showed an id field for
   * something that does not exist yet. Both are nonsense, and both invite a bad value.
   */
  showWhen?: { key: string; equals: string[] };
  /** For payerSelect: which payers to offer. */
  payerFilter?: "medicare_advantage" | "all";
  /**
   * CLE-2811 — a label that depends on the other values (`fieldLabel`). The reimbursement slots read
   * "Reimbursement schedule" on an ASC term, whose one slot needs no qualifier, and "Outpatient
   * reimbursement schedule" where there are two. `label` is the fallback, and what the specs are keyed by.
   */
  labelFor?: (value: Record<string, unknown>) => string;
}

/** The label a field shows for these values: its `labelFor` when it has one, else its `label`. */
export const fieldLabel = (
  spec: IFieldSpec,
  value: Record<string, unknown> | null | undefined
): string => (spec.labelFor && value ? spec.labelFor(value) : spec.label);

/** A contract term's reimbursement slot, labelled for the term's contract type (`reimbursementSlotLabel`). */
const reimbursementSpec = (
  slot: ReimbursementSlot,
  types: string[]
): IFieldSpec => ({
  key: `reimbursement.${slot}`,
  label: reimbursementSlotLabel(slot, null),
  labelFor: (value) =>
    reimbursementSlotLabel(
      slot,
      typeof value.contractType === "string" ? value.contractType : null
    ),
  kind: "textarea",
  fullWidth: true,
  showWhen: { key: "contractType", equals: types },
});

/**
 * Facets we add for the wizard's own routing (which step, which service line). They are not part of
 * any live table, and on a construct that has no field for them they must NOT surface as "the agent
 * emitted something we don't understand" — they came from us.
 */
export const SYSTEM_FACET_KEYS = ["serviceLine", "careSetting"];

/**
 * Scope fields. Required on every payer-scoped component because they are how a recommendation is
 * matched against production — without them, verification silently over-matches across payers.
 * Rendered read-only: they come from the run, and letting a reviewer retype a payer id invites the
 * exact mismatch we're trying to eliminate.
 */
const SCOPE: IFieldSpec[] = [
  { key: "payerId", label: "Payer", kind: "entity", required: true },
  { key: "providerId", label: "Provider", kind: "entity", required: true },
];

const FACETS: IFieldSpec[] = [
  {
    key: "serviceLine",
    label: "Service line",
    kind: "text",
    help: "Used to group this into a review step, and to let you skip service lines you don't want to model.",
  },
  {
    key: "careSetting",
    label: "Care setting",
    kind: "select",
    options: [
      { label: "Inpatient", value: "inpatient" },
      { label: "Outpatient", value: "outpatient" },
      { label: "Both", value: "both" },
    ],
  },
];

/** The agent's note to the reviewer. Read-only: nothing reads it when the construct is built. */
const NOTE: IFieldSpec = {
  key: "note",
  label: "Agent's note",
  kind: "readonly",
  fullWidth: true,
};

const GROUP_NAME: IFieldSpec = {
  key: "groupName",
  label: "Group name",
  kind: "text",
};

const CODES: IFieldSpec = {
  key: "cptCodes",
  label: "CPT / HCPCS codes",
  kind: "codes",
  required: true,
};

/** The charge-relative multiplier trap: the column is a FRACTION, and 85 would price at 8,500%. */
const MULTIPLIER: IFieldSpec = {
  key: "multiplier",
  label: "Multiplier",
  kind: "fraction",
  required: true,
  help: "A decimal fraction of billed charges: enter 0.85 for “85% of charges”, not 85.",
};

const SCOPE_LINE_TOTAL: IFieldSpec = {
  key: "scope",
  label: "Applies to",
  kind: "select",
  required: true,
  options: [
    { label: "Each line's charges", value: "LINE_CHARGES" },
    { label: "The claim's total charges", value: "TOTAL_CHARGES" },
  ],
  help: "Line vs total changes the money — the contract usually says which.",
};

export const FIELD_SPECS: Record<DraftComponentType, IFieldSpec[]> = {
  contract_term: [
    ...SCOPE,
    {
      key: "contractType",
      label: "Contract type",
      kind: "select",
      options: [
        { label: "Hospital", value: "Hospital" },
        { label: "ASC (ambulatory surgery center)", value: "ASC" },
        { label: "Professional (physician)", value: "Professional" },
        { label: "CAH (critical access hospital)", value: "CAH" },
      ],
      help: "Decides which reimbursement boxes below apply.",
    },
    {
      key: "signedDate",
      label: "Signed date",
      kind: "date",
      help: "When the agreement was executed — not the fee effective date below.",
    },
    {
      key: "effectiveDate",
      label: "Effective date",
      kind: "date",
      required: true,
    },
    { key: "endDate", label: "End date", kind: "date" },
    {
      key: "nextUpdateDate",
      label: "Next update / renewal date",
      kind: "date",
    },
    {
      key: "escalator.percentPerYear",
      label: "Annual increase (%)",
      kind: "percent",
    },
    {
      key: "escalator.basis",
      label: "Increase basis",
      kind: "textarea",
      fullWidth: true,
      help: "What the annual increase applies to, in the contract's words.",
    },
    {
      key: "escalator.firstIncreaseOn",
      label: "First increase on",
      kind: "date",
    },
    {
      key: "changeNoticeDays",
      label: "Change notice period (days)",
      kind: "number",
    },
    {
      key: "recoupmentLimitMonths",
      label: "Recoupment limit (months)",
      kind: "number",
    },
    {
      key: "timelyFilingDays",
      label: "Timely filing (days from DOS)",
      kind: "number",
    },
    {
      key: "implantTerms",
      label: "Implant terms",
      kind: "textarea",
      fullWidth: true,
      help: "How implants are reimbursed, in the contract's words. Blank means the contract is silent — which is not the same as 'not paid'.",
    },
    reimbursementSpec("inpatient", ["Hospital", "CAH"]),
    reimbursementSpec("outpatient", ["Hospital", "CAH", "ASC"]),
    reimbursementSpec("facility", ["Professional"]),
    reimbursementSpec("nonFacility", ["Professional"]),
    {
      key: "multipleProcedureMethodology",
      label: MULTIPLE_PROCEDURE_METHODOLOGY,
      kind: "textarea",
      fullWidth: true,
      help: "Saved into the contract's own field of the same name. Recorded only: the MPPR rules that actually price are built separately, from provider + payer.",
    },
    NOTE,
  ],

  base_rate_file: [
    {
      key: "action",
      label: "How we get this file",
      kind: "select",
      required: true,
      fullWidth: true,
      options: [
        { label: "Reuse a file already loaded", value: "reuse_existing" },
        { label: "Generate from CMS (Medicare physician)", value: "generate" },
        {
          label: "Generate from the contract's rate sheet",
          value: "generate_from_contract",
        },
        {
          label: "Generate as a % of a file we already hold",
          value: "generate_from_existing",
        },
        {
          label: "Generate one file, a different % per service line",
          value: "generate_percent_by_service_line",
        },
        { label: "A human must upload it", value: "upload_required" },
      ],
    },
    // Reusing a loaded file: pick WHICH one from a dropdown of the account's files. Selecting sets both
    // displayName and baseRateFileId, so the reviewer never hand-types a name (or sees the raw uuid).
    {
      key: "displayName",
      label: "Base rate file",
      kind: "baseRateFileReuseSelect",
      required: true,
      fullWidth: true,
      showWhen: { key: "action", equals: ["reuse_existing"] },
    },
    // Generating/uploading a NEW file: displayName is the name to give it, so it's free text.
    {
      key: "displayName",
      label: "Base rate file name",
      kind: "text",
      required: true,
      fullWidth: true,
      showWhen: {
        key: "action",
        equals: [
          "generate",
          "generate_from_contract",
          "generate_from_existing",
          "generate_percent_by_service_line",
          "upload_required",
        ],
      },
    },
    // Every field below is gated on `action`, so the form shows only what the chosen path needs. Without
    // this it dumped every union field at once (GPCIs, year) onto an upload_required recommendation,
    // which was baffling — none of them applied.
    {
      key: "feeScheduleType",
      label: "Fee schedule type",
      kind: "select",
      showWhen: { key: "action", equals: ["generate"] },
      options: [
        { label: "Physician — facility", value: "PHYSICIAN_FACILITY" },
        { label: "Physician — non-facility", value: "PHYSICIAN_NON_FACILITY" },
      ],
    },
    {
      key: "year",
      label: "Year",
      kind: "number",
      showWhen: { key: "action", equals: ["generate"] },
    },
    {
      key: "gpci.pw",
      label: "GPCI — work",
      kind: "number",
      showWhen: { key: "action", equals: ["generate"] },
    },
    {
      key: "gpci.pe",
      label: "GPCI — practice expense",
      kind: "number",
      showWhen: { key: "action", equals: ["generate"] },
    },
    {
      key: "gpci.mp",
      label: "GPCI — malpractice",
      kind: "number",
      showWhen: { key: "action", equals: ["generate"] },
    },
    {
      key: "applySequester",
      label: "Apply sequestration",
      kind: "boolean",
      showWhen: { key: "action", equals: ["generate"] },
    },
    {
      key: "sourceDisplayName",
      label: "File to take a % of",
      kind: "baseRateFileSelect",
      fullWidth: true,
      showWhen: {
        key: "action",
        equals: ["generate_from_existing", "generate_percent_by_service_line"],
      },
      help: "Pick the loaded fee schedule this is a percentage of.",
    },
    {
      key: "percentOfSource",
      label: "Percent of that file",
      kind: "percent",
      showWhen: { key: "action", equals: ["generate_from_existing"] },
      help: "Whole percent — 160 = 160% of the source file's rates.",
    },
    {
      key: "lines",
      label: "Percent by service line",
      kind: "percentRows",
      fullWidth: true,
      showWhen: { key: "action", equals: ["generate_percent_by_service_line"] },
      help: "Each service line and its whole-percent multiple of the file above (189 = 189%). Codes in no line use the catch-all below.",
    },
    {
      key: "catchAllPercent",
      label: "Catch-all percent (all other codes)",
      kind: "percent",
      showWhen: { key: "action", equals: ["generate_percent_by_service_line"] },
      help: "Applied to every code not in a service line above — the “All Services not otherwise identified” rate.",
    },
    {
      key: "claimType",
      label: "Service-line library",
      kind: "select",
      showWhen: { key: "action", equals: ["generate_percent_by_service_line"] },
      options: [
        { label: "Professional", value: "PROFESSIONAL" },
        { label: "Outpatient", value: "OUTPATIENT" },
        { label: "Inpatient", value: "INPATIENT" },
      ],
      help: "Which per-payer library maps CPT codes to service lines.",
    },
    {
      // The agent's citation of where the rate sheet came from. Nothing in generation reads it (the CSV
      // is built from sourceDisplayName + percentOfSource), so it's read-only like the agent's note —
      // an editable box implied the reviewer was meant to fill it, which they aren't.
      key: "sourceDocument",
      label: "Source document",
      kind: "readonly",
      fullWidth: true,
      help: "Where the agent read this rate sheet. Reference only — not used to build the file.",
      showWhen: {
        key: "action",
        equals: ["generate_from_contract", "generate_from_existing"],
      },
    },
    {
      key: "effectiveDate",
      label: "Effective date",
      kind: "date",
      showWhen: {
        key: "action",
        equals: [
          "generate_from_contract",
          "generate_from_existing",
          "generate_percent_by_service_line",
        ],
      },
    },
    {
      key: "reason",
      label: "Why a human must supply it",
      kind: "readonly",
      fullWidth: true,
      showWhen: { key: "action", equals: ["upload_required"] },
    },
    // No serviceLine facet here: a base rate file is one contract-wide CPT→rate file, not a per-service-
    // line rate. (The per-line variant is the `generate_percent_by_service_line` action's `lines[]`, above.)
    NOTE,
  ],

  rate_calculation: [
    ...SCOPE,
    {
      key: "baseRateFileDisplayName",
      label: "Base rate file",
      kind: "text",
      required: true,
      fullWidth: true,
    },
    {
      key: "rateType",
      label: "Rate type",
      kind: "select",
      required: true,
      fullWidth: true,
      options: [
        { label: "Percent of the base rate file", value: "percent" },
        { label: "Dollars per anesthesia unit", value: "unit" },
      ],
      help: "Percent means 102 = 102% of base. Unit means the rate is dollars per anesthesia unit.",
    },
    {
      key: "ratePercent",
      label: "Rate",
      kind: "percent",
      required: true,
      help: "Whole percent when rate type is Percent (102 = 102% of base).",
    },
    {
      key: "sortOrder",
      label: "Sort order",
      kind: "number",
      help: "Order this calc stacks in.",
    },
    ...FACETS,
    NOTE,
  ],

  carveout: [
    ...SCOPE,
    {
      key: "baseRateFileDisplayName",
      label: "Carveout file",
      kind: "text",
      required: true,
      // A file name, and a long one — in a shared-row cell it scrolls out of sight.
      fullWidth: true,
    },
    {
      key: "rateType",
      label: "Rate type",
      kind: "select",
      options: [
        { label: "Percent", value: "percent" },
        { label: "Unit", value: "unit" },
      ],
    },
    {
      key: "ratePercent",
      label: "Rate",
      kind: "percent",
      help: "Omit for 100%.",
    },
    {
      key: "rows",
      label: "Carved-out codes",
      kind: "moneyRows",
      fullWidth: true,
      help: "Each code and the dollar amount it pays. These rows ARE the carveout.",
    },
    ...FACETS,
    NOTE,
  ],

  lesser_of_charges: [
    ...SCOPE,
    SCOPE_LINE_TOTAL,
    MULTIPLIER,
    GROUP_NAME,
    ...FACETS,
    NOTE,
  ],

  charge_based_pricing: [
    ...SCOPE,
    SCOPE_LINE_TOTAL,
    {
      ...MULTIPLIER,
      help: "A decimal fraction of billed charges: 0.45 = “45% of charges”.",
    },
    {
      key: "maxAmountInCents",
      label: "Maximum payment",
      kind: "money",
      help: "Leave blank for no cap.",
    },
    GROUP_NAME,
    ...FACETS,
    NOTE,
  ],

  mpdr_ruleset: [
    ...SCOPE,
    CODES,
    {
      key: "discountPercentForRank2",
      label: "2nd procedure — % paid",
      kind: "percent",
      required: true,
      help: "The percent PAID at this rank. 50 means the 2nd procedure pays 50% of its rate.",
    },
    {
      key: "discountPercentForRank3",
      label: "3rd procedure — % paid",
      kind: "percent",
      required: true,
    },
    {
      key: "discountPercentForRank4",
      label: "4th procedure — % paid",
      kind: "percent",
      required: true,
    },
    {
      key: "discountPercentForRank5Plus",
      label: "5th+ procedure — % paid",
      kind: "percent",
      required: true,
    },
    {
      key: "allowOnDuplicateCptCodes",
      label: "Apply to duplicate CPT codes",
      kind: "boolean",
    },
    {
      key: "duplicateNotPaid",
      label: "Duplicates are not paid",
      kind: "boolean",
    },
    GROUP_NAME,
    ...FACETS,
    NOTE,
  ],

  case_rate_ruleset: [
    ...SCOPE,
    {
      ...GROUP_NAME,
      required: true,
      help: "Required, and unique for this provider + payer.",
    },
    CODES,
    { key: "excludedCptCodes", label: "Excluded CPT codes", kind: "codes" },
    { key: "isPerDiem", label: "Per diem", kind: "boolean" },
    { key: "isBilateral", label: "Bilateral", kind: "boolean" },
    ...FACETS,
    NOTE,
  ],

  package_ruleset: [
    ...SCOPE,
    {
      key: "triggerCptCodes",
      label: "Trigger CPT codes",
      kind: "codes",
      required: true,
    },
    {
      key: "bundledCptCodes",
      label: "Bundled CPT codes",
      kind: "codes",
      required: true,
    },
    GROUP_NAME,
    ...FACETS,
    NOTE,
  ],

  exploding_fee_schedule: [
    ...SCOPE,
    { ...CODES, help: "Between 2 and 15 codes." },
    {
      key: "totalRateInCents",
      label: "Total rate",
      kind: "money",
      required: true,
    },
    GROUP_NAME,
    ...FACETS,
    NOTE,
  ],

  unit_cap_ruleset: [
    ...SCOPE,
    CODES,
    {
      key: "maxUnitsPerCode",
      label: "Max units per code",
      kind: "number",
      required: true,
      help: "A count of units, not money. 1 = “once per service”.",
    },
    GROUP_NAME,
    ...FACETS,
    NOTE,
  ],

  inpatient_ruleset: [
    ...SCOPE,
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "drgCodes", label: "DRG codes", kind: "codes", required: true },
    {
      key: "config.type",
      label: "Type",
      kind: "select",
      required: true,
      options: [
        { label: "Fixed cost outlier", value: "FIXED_COST" },
        { label: "Pass-through", value: "PASS_THROUGH" },
        { label: "Stop loss", value: "STOP_LOSS" },
        { label: "Charge outlier", value: "CHARGE_OUTLIER" },
        { label: "Excessive length of stay", value: "EXCESSIVE_LOS" },
        { label: "Transfer", value: "TRANSFER" },
        { label: "Inpatient outlier", value: "INPATIENT_OUTLIER" },
      ],
    },
    ...FACETS,
    NOTE,
  ],

  payer_consolidation: [
    {
      key: "action",
      label: "What to do",
      kind: "select",
      required: true,
      fullWidth: true,
      options: [
        { label: "Use an existing payer", value: "use_existing_payer" },
        { label: "Create a new payer", value: "create_payer" },
        { label: "Split into separate payers", value: "split_payer" },
        { label: "No action needed", value: "no_action" },
      ],
    },
    {
      key: "payerId",
      label: "Which payer",
      kind: "payerSelect",
      payerFilter: "all",
      required: true,
      fullWidth: true,
      showWhen: { key: "action", equals: ["use_existing_payer"] },
    },
    {
      key: "payerName",
      label: "Name for the new payer",
      kind: "text",
      required: true,
      fullWidth: true,
      showWhen: { key: "action", equals: ["create_payer", "split_payer"] },
    },
    NOTE,
  ],

  ma_payer_link: [
    {
      key: "action",
      label: "What to do",
      kind: "select",
      required: true,
      fullWidth: true,
      options: [
        {
          label: "Link an existing Medicare Advantage payer",
          value: "link_existing_ma_payer",
        },
        {
          label: "Create a new Medicare Advantage payer",
          value: "create_ma_payer",
        },
        { label: "Don't model Medicare Advantage", value: "not_modeled" },
      ],
      help: "How this contract's Medicare Advantage business is routed.",
    },
    {
      // Link: pick from the payers that actually exist. No typing.
      key: "payerId",
      label: "Which payer",
      kind: "payerSelect",
      payerFilter: "medicare_advantage",
      required: true,
      fullWidth: true,
      showWhen: { key: "action", equals: ["link_existing_ma_payer"] },
      help: "Only Medicare Advantage payers are listed.",
    },
    {
      // Create: it doesn't exist yet, so there is no id to give — only a name.
      key: "payerName",
      label: "Name for the new payer",
      kind: "text",
      required: true,
      fullWidth: true,
      showWhen: { key: "action", equals: ["create_ma_payer"] },
      help: "You'll create this payer in Payer Rules; nothing is created automatically.",
    },
    { key: "reason", label: "Why", kind: "readonly", fullWidth: true },
    NOTE,
  ],

  // Legacy: the worker no longer emits it (runs are single-provider). Kept so old rows still render.
  provider_decomposition: [NOTE],
};

/**
 * `inpatient_ruleset.config` is a trigger x formula union with ~7 triggers and ~6 formulas, and the API
 * schema accepts it as an object rather than guessing at it. Rendering a form for a shape we haven't
 * typed would be inventing a UI for rules we don't understand — so the reviewer edits it as raw JSON,
 * and the server still validates the parts we DO know.
 */
export const RAW_JSON_FIELDS: Partial<Record<DraftComponentType, string[]>> = {
  inpatient_ruleset: ["config"],
  // carveout.rows is now a friendly add/remove editor (kind "moneyRows"), not raw JSON.
};

export const fieldSpecsFor = (
  componentType: DraftComponentType
): IFieldSpec[] => FIELD_SPECS[componentType] ?? [NOTE];
